import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useClient, useLoading, useQuery } from '@based/sdk/react'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import { InitiativeSearchGrid } from '../sections/InitiativeSearchGrid'
import { match } from 'ts-pattern'
import { SearchMap } from '../sections/SearchMap'
import { useSearchParams } from 'wouter'
import {
  DiscoveryCrawlerPaginationFooter,
  useDiscoveryCrawlerPage,
  usePageIndex,
} from '@app/util/hooks/useDiscoveryCrawlerPage'
import { pages } from '@app/util/links'
import type { InitiativeFilter } from 'functions/app/initiative/list/filters'
import type { ListInitiativeModel } from 'functions/app/initiative/list'
import { InitiativeFilterDrawer } from '../sections/InitiativeFilterDrawer'
import {
  useQueryStates,
  parseAsArrayOf,
  parseAsInteger,
  parseAsBoolean,
  parseAsString,
} from 'nuqs'
import type { ListPhaseModel } from 'functions/app/dashboard/supporting-entities/phase/list'
import type { InitiativeSpotlightModel } from 'functions/app/spotlight/list-initiatives'
import { SectionHeader } from '@app/components/blocks/sectionHeader/SectionHeader'
import Column from '@app/components/layout/Column'
import { SpotlightInitiativeGrid } from '@app/components/blocks/spotlightInitiativeGrid/SpotlightInitiativeGrid'
import { Empty } from '@app/components/blocks/empty/Empty'
import FindLocationImage from '@app/assets/images/find-location.png'

export const DiscoverInitiatives = ({
  view,
  searchTerm,
  onResetSearch,
  filtersOpen,
  onFiltersClose,
}: {
  view: 'grid' | 'map'
  searchTerm: string
  onResetSearch: () => void
  filtersOpen: boolean
  onFiltersClose: () => void
}) => {
  const PAGE_SIZE = 15
  const t = useTranslations()
  const translations = t.app.discoveryPage.tabs.initiatives
  const commonTranslations = t.app.common
  const requestedPage = usePageIndex()
  const fetchTop = requestedPage * PAGE_SIZE
  const [total, setTotal] = useState(0)
  const [initiatives, setInitiatives] = useState<ListInitiativeModel[]>([])
  const [top, setTop] = useState(fetchTop)
  const [mounted, setMounted] = useState(false)
  const client = useClient()
  const isLoading = useLoading()
  const [searchParams] = useSearchParams()
  const isSearchActive = searchTerm.length > 0
  const [filterParams, setFilterParams] = useQueryStates({
    phase: parseAsArrayOf(parseAsInteger),
    developmentForm: parseAsArrayOf(parseAsInteger),
    searchingForMembers: parseAsBoolean,
    applyPhase: parseAsString,
  })
  const { data: phases } = useQuery('list-phase')
  const { data: spotlightInitiatives } = useQuery(
    'list-spotlight-initiatives',
    {
      searchTerm: searchTerm,
      filters: filterParams,
    },
  ) as { data?: InitiativeSpotlightModel[] }

  const initiativesToExclude: number[] = useMemo(() => {
    return isSearchActive
      ? []
      : (spotlightInitiatives ?? []).map((s) => s.initiative.id)
  }, [spotlightInitiatives, isSearchActive])

  const getApplyPhase = searchParams.get('applyPhase')
  useEffect(() => {
    const phaseType = filterParams.applyPhase ?? getApplyPhase
    if (!phaseType || !phases?.length) {
      return
    }
    const phase = phases.find((p: ListPhaseModel) => p.phaseType === phaseType)
    if (phase) {
      void setFilterParams({
        phase: [phase.id],
        applyPhase: null,
      })
    } else {
      void setFilterParams({ applyPhase: null })
    }
  }, [filterParams.applyPhase, getApplyPhase, phases, setFilterParams])

  // Wrapper to match InitiativeFilterDrawer's expected setFilters signature
  const setFilters = useCallback(
    (newFilters: InitiativeFilter) => {
      void setFilterParams({
        phase: newFilters.phase ?? null,
        developmentForm: newFilters.developmentForm ?? null,
        searchingForMembers: newFilters.searchingForMembers ?? null,
      })
    },
    [setFilterParams],
  )

  // useQuery is for SSR and client side queries will be managed by client.query
  const dataServerQuery = useQuery('list-initiative', {
    searchTerm: searchTerm,
    top: fetchTop,
    desc: true,
    initiativesToExclude,
  })

  const serverInitiatives: ListInitiativeModel[] = dataServerQuery.data ?? []

  const renderedInitiatives = mounted ? initiatives : serverInitiatives
  const { nextPageHref } = useDiscoveryCrawlerPage({
    hasMore: serverInitiatives.length >= fetchTop,
  })

  const query = useCallback(
    (topParam: number) => {
      const close = client
        .query('list-initiative', {
          searchTerm: searchTerm,
          top: topParam,
          filters: filterParams,
          desc: true,
          initiativesToExclude,
        })
        .subscribe((result) => {
          setInitiatives(result)
          setMounted(true)
        })
      return () => close()
    },
    [client, searchTerm, filterParams, initiativesToExclude],
  )
  const countQuery = useCallback(() => {
    const close = client
      .query('list-initiative', {
        searchTerm: searchTerm,
        filters: filterParams,
        count: true,
        initiativesToExclude,
      })
      .subscribe((r) => setTotal(r.count))
    return () => close()
  }, [client, searchTerm, filterParams, initiativesToExclude])

  useEffect(() => {
    setTop(fetchTop)
    const cleanupCount = countQuery()
    const cleanupQuery = query(fetchTop)
    return () => {
      cleanupCount()
      cleanupQuery()
    }
  }, [searchTerm, filterParams, fetchTop, countQuery, query])

  useEffect(() => {
    if (top === fetchTop) {
      return
    }
    const cleanup = query(top)
    return () => cleanup()
  }, [top, query, fetchTop])

  const currentTotal = mounted ? total : serverInitiatives.length
  const hasSpotlights = !isSearchActive && spotlightInitiatives?.length > 0
  const spotlightPrimaryFlags = useMemo(
    () => (spotlightInitiatives ?? []).map((s) => Boolean(s.isPrimary)),
    [spotlightInitiatives],
  )

  return (
    <>
      <InitiativeFilterDrawer
        open={filtersOpen}
        onClose={onFiltersClose}
        filters={filterParams}
        setFilters={setFilters}
      />

      {hasSpotlights && (
        <Column alignItems="stretch">
          <SectionHeader
            title={translations.spotlight.title}
            subtitle={translations.spotlight.subTitle}
          />
          <SpotlightInitiativeGrid
            initiatives={(spotlightInitiatives ?? []).map((s) => s.initiative)}
            isPrimaryByIndex={spotlightPrimaryFlags}
            notSpecified={commonTranslations.notSpecified}
            searchingForMembersLabel={
              commonTranslations.initiativeStatus.searchingForMembers
            }
            notSearchingForMembersLabel={
              commonTranslations.initiativeStatus.notSearchingForMembers
            }
          />
        </Column>
      )}

      {renderedInitiatives.length === 0 && !isLoading ? (
        <Empty
          title={translations.emptyTitle}
          imageSrc={FindLocationImage}
          style={{ marginTop: '4rem' }}
        >
          {translations.emptyDescription}
        </Empty>
      ) : (
        match(view)
          .with('grid', () => (
            <InitiativeSearchGrid
              items={renderedInitiatives.map((initiative) => ({
                id: initiative.slug,
                to: pages.initiative(initiative.slug),
                image: initiative.images?.[0]?.src ?? defaultProjectPicture,
                title: initiative.title,
                description: initiative.description,
                address: initiative.location.address,
                phase: initiative.phase?.name,
                tagText: initiative.searchingForMembers
                  ? commonTranslations.initiativeStatus.searchingForMembers
                  : commonTranslations.initiativeStatus.notSearchingForMembers,
                tagVariant: initiative.searchingForMembers
                  ? 'success'
                  : 'neutral',
              }))}
              totalItems={currentTotal}
              title={translations.title}
              subtitle={translations.subTitle}
              isLoading={isLoading}
              searchTerm={searchTerm}
              loadMore={() => setTop((prev) => prev + PAGE_SIZE)}
              onResetSearch={onResetSearch}
              footer={
                <DiscoveryCrawlerPaginationFooter nextPageHref={nextPageHref} />
              }
            />
          ))
          .with('map', () => (
            <SearchMap
              items={initiatives.map((initiative) => ({
                id: initiative.uuid,
                image: initiative.images?.[0]?.src ?? defaultProjectPicture,
                title: initiative.title,
                description: initiative.description,
                address: initiative.location.address,
                status: initiative.phase.name,
                lat: initiative.location.geo.lat,
                long: initiative.location.geo.long,
              }))}
              totalItems={currentTotal}
              title={translations.title}
              subtitle={translations.subTitle}
              isLoading={isLoading}
              searchTerm={searchTerm}
              loadMore={() => setTop((prev) => prev + PAGE_SIZE)}
              onResetSearch={onResetSearch}
            />
          ))
          .exhaustive()
      )}
    </>
  )
}
